import { createRoot } from 'react-dom/client';
import { App } from 'ts/App';
import { NavigationHash } from 'ts/commons/NavigationHash';
import Redirects from 'ts/Redirects';
import '../tailwind.css';
import './../styles/main.less';
import './components/theme.css';

/** Redirects if the URL is rewritten for backwards compatibility. */
function redirectForBackwardsCompatibleURLs(): boolean {
	const maybeRedirect = Redirects.createRedirectOrNull(window.location.href);
	if (maybeRedirect !== null) {
		window.location.replace(maybeRedirect);
	}
	return Boolean(maybeRedirect);
}

/** Main entry point into Teamscale which parses the current path, loads and shows the corresponding perspective. */
function showPerspective(): void {
	if (redirectForBackwardsCompatibleURLs()) {
		// The current page may render too quickly before the redirection process starts.
		return;
	}

	const appRoot = document.getElementById('app')!;
	const root = createRoot(appRoot);
	root.render(<App />);
}

// When we deploy a new version of Teamscale, users might still have parts of the webapp cached locally.
// The dynamically imported modules might, however, no longer exist under the same name in the current version.
// Hence, when we run into such a case, we need to do a full page reload to get a consistent set of JS files (TS-43119).
// See https://vite.dev/guide/build#load-error-handling
if (import.meta.env.PROD) {
	window.addEventListener('vite:preloadError', e => {
		const key = 'last-force-full-reload';
		const lastReload = window.sessionStorage.getItem(key);
		// Avoid a reload loop in case the error does persist even after the reload
		const shouldReload = lastReload == null || Number(lastReload) + 5000 < Date.now();
		if (!shouldReload) {
			return;
		}
		console.warn('Caught a preloadError. Will try to recover with a reload.', e.payload);
		window.sessionStorage.setItem(key, String(Date.now()));
		const hash = NavigationHash.getCurrent();
		hash.toggleReload();
		window.location.assign(hash.toFullUrl());
	});
}

document.addEventListener('DOMContentLoaded', showPerspective);
